import React from 'react';
import { useNavigate } from 'react-router-dom';
import { linkedItemPath, formatINR } from '../../constants/garmentCodes';

const MONTHS_SHOWN = 6;

const monthLabel = (ym) => {
  const d = new Date(`${ym}-01`);
  return Number.isNaN(d.getTime()) ? ym : d.toLocaleDateString('en-IN', { month: 'short' });
};

// `months` is DashboardSummary.monthlyLedger: [{ month: "YYYY-MM", credit, debit }], oldest first.
export function MonthlyLedgerChart({ months = [] }) {
  const recent = (months || []).slice(-MONTHS_SHOWN);
  const max = Math.max(1, ...recent.flatMap((m) => [Number(m.credit) || 0, Number(m.debit) || 0]));
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-semibold text-gray-900">Monthly Financial Activity</h3>
          <p className="text-[11px] text-gray-400">Credits vs debits — all engagements</p>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-green-400 rounded-sm inline-block" />Credits</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-red-300 rounded-sm inline-block" />Debits</span>
        </div>
      </div>
      {recent.length === 0 ? (
        <p className="h-32 flex items-center justify-center text-[12px] text-gray-400">No ledger activity yet</p>
      ) : (
        <div className="flex items-end gap-3 h-32">
          {recent.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end gap-0.5 h-24">
                <div className="flex-1 bg-green-400 rounded-t-sm" style={{ height: `${((Number(m.credit) || 0) / max) * 100}%` }} title={`Credits: ${formatINR(m.credit)}`} />
                <div className="flex-1 bg-red-300 rounded-t-sm" style={{ height: `${((Number(m.debit) || 0) / max) * 100}%` }} title={`Debits: ${formatINR(m.debit)}`} />
              </div>
              <span className="text-[10px] text-gray-400">{monthLabel(m.month)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// `items` is DashboardSummary.recentActivity (AuditLog rows, newest first).
export function RecentActivity({ items = [], basePath }) {
  const navigate = useNavigate();
  const list = items || [];
  return (
    <div className="bg-white rounded-lg border border-gray-100">
      <div className="px-5 py-3 border-b border-gray-50">
        <h3 className="text-[13px] font-semibold text-gray-900">Recent Activity</h3>
      </div>
      <div className="px-5 py-3 space-y-3">
        {list.length === 0 && <p className="text-center text-[12px] text-gray-400 py-3">No recent activity</p>}
        {list.map((a) => {
          const path = linkedItemPath(basePath, a.entityType);
          return (
            <button key={a.id} onClick={() => path && navigate(path)} className="w-full text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold bg-blue-100 text-blue-600">●</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-700 leading-snug">{a.summary || [a.action, a.entityType].filter(Boolean).join(' · ')}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{[a.userName, a.brandName].filter(Boolean).join(' · ')}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
